import type { FC, FormEvent, ReactNode } from 'react'
import type { ContactIntent } from '@/constants/contact'

import { createContext, useCallback, useContext, useId, useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Modal } from '@/components/generals/modal'
import { LogoMark } from '@/components/generals/logo'
import { PillButton } from '@/components/generals/pill-button'
import { contactIntents } from '@/constants/contact'

interface ContactOptions {
    intent?: ContactIntent
    message?: string
}

interface ContactContextValue {
    openContact: (options?: ContactOptions) => void
}

const ContactContext = createContext<ContactContextValue>({ openContact: () => undefined })

export const useContact = (): ContactContextValue => useContext(ContactContext)

interface ContactDialogProps {
    open: boolean
    intent: ContactIntent
    initialMessage: string
    onClose: () => void
}

type Status = 'idle' | 'sending' | 'sent' | 'error'

const inputClasses = 'block w-full rounded-xl bg-white px-3.5 text-[15px] text-ink ring-1 ring-inset ring-[#e3e3e9] transition-shadow placeholder:text-[#9a9aa4] hover:ring-[#d4d4dc] focus:outline-none focus:ring-2 focus:ring-violet'

const ContactDialog: FC<ContactDialogProps> = ({ open, intent, initialMessage, onClose }): ReactNode => {
    const config = contactIntents[intent]
    const id = useId()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [company, setCompany] = useState('')
    const [message, setMessage] = useState(initialMessage)
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        setStatus('sending')
        setError('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    intent,
                    name,
                    email,
                    company,
                    message,
                    website,
                    page: window.location.pathname
                })
            })

            const payload = (await response.json().catch(() => null)) as { ok?: boolean, message?: string } | null

            if (!response.ok || !payload?.ok) {
                setStatus('error')
                setError(payload?.message ?? 'Something went wrong. Please try again.')
                return
            }

            setStatus('sent')
        }
        catch {
            setStatus('error')
            setError('We could not reach our server. Check your connection and try again.')
        }
    }

    const firstName = name.trim().split(' ')[0]

    return (
        <Modal open={open} onClose={onClose} labelledBy={`${id}-title`} variant='sheet'>
            {status === 'sent' ? (
                <div className='flex flex-col items-start gap-5 px-6 pb-7 pt-10 sm:px-8 sm:pb-8'>
                    <span className='inline-flex size-12 items-center justify-center rounded-full bg-mint text-forest'>
                        <Check className='size-6' aria-hidden='true' />
                    </span>

                    <div className='flex flex-col gap-2'>
                        <h2 id={`${id}-title`} className='text-2xl font-medium tracking-[-0.03em]'>
                            Thanks{firstName ? `, ${firstName}` : ''}.
                        </h2>

                        <p className='text-[15px] leading-relaxed text-slate'>
                            We received your message and will reply to <span className='font-medium text-ink'>{email}</span>.
                        </p>
                    </div>

                    <PillButton onClick={onClose} variant='secondary' size='md'>
                        Close
                    </PillButton>
                </div>
            ) : (
                <form onSubmit={handleSubmit} noValidate={false} className='flex flex-col gap-6 px-6 pb-6 pt-8 sm:px-8 sm:pb-8 sm:pt-9'>
                    <div className='flex flex-col gap-3 pr-8'>
                        <span className='inline-flex size-10 items-center justify-center rounded-xl bg-violet-soft text-ink'>
                            <LogoMark className='h-4 w-[22px]' />
                        </span>

                        <h2 id={`${id}-title`} className='text-[26px] font-medium leading-tight tracking-[-0.03em]'>
                            {config.title}
                        </h2>

                        <p className='text-[15px] leading-relaxed text-slate'>
                            {config.description}
                        </p>
                    </div>

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <label className='flex flex-col gap-1.5'>
                            <span className='text-[13px] font-medium text-graphite'>
                                Full name
                            </span>

                            <input
                                data-autofocus
                                required
                                type='text'
                                name='name'
                                autoComplete='name'
                                value={name}
                                onChange={event => setName(event.target.value)}
                                className={cn(inputClasses, 'h-11')}
                                placeholder='Jordan Ellis'
                            />
                        </label>

                        <label className='flex flex-col gap-1.5'>
                            <span className='text-[13px] font-medium text-graphite'>
                                Work email
                            </span>

                            <input
                                required
                                type='email'
                                name='email'
                                autoComplete='email'
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                className={cn(inputClasses, 'h-11')}
                                placeholder='jordan@company.com'
                            />
                        </label>

                        {!config.compact && (
                            <label className='flex flex-col gap-1.5 sm:col-span-2'>
                                <span className='text-[13px] font-medium text-graphite'>
                                    Company <span className='font-normal text-silver'>(optional)</span>
                                </span>

                                <input
                                    type='text'
                                    name='company'
                                    autoComplete='organization'
                                    value={company}
                                    onChange={event => setCompany(event.target.value)}
                                    className={cn(inputClasses, 'h-11')}
                                    placeholder='Company name'
                                />
                            </label>
                        )}

                        {!config.compact && (
                            <label className='flex flex-col gap-1.5 sm:col-span-2'>
                                <span className='text-[13px] font-medium text-graphite'>
                                    {config.messageLabel} <span className='font-normal text-silver'>(optional)</span>
                                </span>

                                <textarea
                                    name='message'
                                    rows={4}
                                    value={message}
                                    onChange={event => setMessage(event.target.value)}
                                    className={cn(inputClasses, 'resize-none py-3 leading-relaxed')}
                                    placeholder={config.messagePlaceholder}
                                />
                            </label>
                        )}

                        <label aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                            Website
                            <input
                                type='text'
                                name='website'
                                tabIndex={-1}
                                autoComplete='off'
                                value={website}
                                onChange={event => setWebsite(event.target.value)}
                            />
                        </label>
                    </div>

                    {status === 'error' && (
                        <p role='alert' className='rounded-xl bg-[#fdecea] px-4 py-3 text-sm text-[#b42318]'>
                            {error}
                        </p>
                    )}

                    <div className='flex flex-col-reverse items-stretch gap-4 sm:flex-row sm:items-center sm:justify-between'>
                        <p className='text-[13px] leading-snug text-silver'>
                            We only use your details to reply to you.
                        </p>

                        <PillButton type='submit' chevron disabled={status === 'sending'}>
                            {status === 'sending' ? 'Sending...' : config.submit}
                        </PillButton>
                    </div>
                </form>
            )}
        </Modal>
    )
}

interface ContactState {
    open: boolean
    intent: ContactIntent
    message: string
    key: number
}

export const ContactProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [state, setState] = useState<ContactState>({ open: false, intent: 'contact', message: '', key: 0 })

    const openContact = useCallback((options: ContactOptions = {}) => {
        setState(previous => ({
            open: true,
            intent: options.intent ?? 'contact',
            message: options.message ?? '',
            key: previous.key + 1
        }))
    }, [])

    const close = useCallback(() => {
        setState(previous => ({ ...previous, open: false }))
    }, [])

    const value = useMemo(() => ({ openContact }), [openContact])

    return (
        <ContactContext.Provider value={value}>
            {children}

            <ContactDialog
                key={state.key}
                open={state.open}
                intent={state.intent}
                initialMessage={state.message}
                onClose={close}
            />
        </ContactContext.Provider>
    )
}
